import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type State =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'already' }
  | { kind: 'error'; message: string }
  | { kind: 'success' };

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: 'Missing unsubscribe token.' });
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_ANON_KEY } },
        );
        const data = await res.json();
        if (!res.ok) {
          setState({ kind: 'error', message: data.error || 'Invalid link.' });
          return;
        }
        if (data.valid === false && data.reason === 'already_unsubscribed') {
          setState({ kind: 'already' });
          return;
        }
        if (data.valid) {
          setState({ kind: 'ready' });
          return;
        }
        setState({ kind: 'error', message: 'Invalid link.' });
      } catch (e) {
        setState({ kind: 'error', message: 'Network error. Please try again.' });
      }
    })();
  }, [token]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'handle-email-unsubscribe',
        { body: { token } },
      );
      if (error) {
        setState({ kind: 'error', message: error.message });
      } else if (data?.success) {
        setState({ kind: 'success' });
      } else if (data?.reason === 'already_unsubscribed') {
        setState({ kind: 'already' });
      } else {
        setState({ kind: 'error', message: 'Could not unsubscribe.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Email preferences</CardTitle>
          <CardDescription>Unsubscribe from notification emails</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {state.kind === 'loading' && <p className="text-sm text-muted-foreground">Validating link…</p>}
          {state.kind === 'ready' && (
            <>
              <p className="text-sm">
                Click below to stop receiving notification emails from Label Batch Designer.
              </p>
              <Button onClick={handleConfirm} disabled={submitting} className="w-full">
                {submitting ? 'Unsubscribing…' : 'Confirm unsubscribe'}
              </Button>
            </>
          )}
          {state.kind === 'already' && (
            <p className="text-sm text-muted-foreground">
              You have already unsubscribed. No further emails will be sent.
            </p>
          )}
          {state.kind === 'success' && (
            <p className="text-sm text-foreground">
              You've been unsubscribed. We won't send you notification emails anymore.
            </p>
          )}
          {state.kind === 'error' && (
            <p className="text-sm text-destructive">{state.message}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}